/**
 * Gross Margin by Branch Chart Component
 * Bar chart showing gross margin % per branch
 */

import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { ChartExportMenu } from './ChartExportMenu'
import { exportToCSV, exportToExcel, exportToPNG, generateFilename } from '@/lib/utils/export-utils'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'

export interface BranchMarginData {
  branch: string
  grossMarginPercent: number
}

interface GrossMarginByBranchChartProps {
  data: BranchMarginData[]
  engagementId?: string | number
}

const BRANCH_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#f43f5e', // rose
  '#06b6d4', // cyan
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null
  const value: number = payload[0].value
  return (
    <div className="bg-background border rounded-md shadow-sm px-3 py-2 text-xs">
      <p className="font-medium mb-1">{label}</p>
      <p className="text-muted-foreground">
        Gross Margin: <span className="font-semibold text-foreground">{value.toFixed(1)}%</span>
      </p>
    </div>
  )
}

export function GrossMarginByBranchChart({
  data,
  engagementId = 'portfolio',
}: GrossMarginByBranchChartProps) {
  const handleExportCSV = () => {
    const exportData = data.map(d => ({ Branch: d.branch, 'Gross Margin %': d.grossMarginPercent }))
    exportToCSV(exportData, generateFilename('GrossMarginByBranch', engagementId, 'csv'))
  }

  const handleExportExcel = () => {
    const exportData = data.map(d => ({ Branch: d.branch, 'Gross Margin %': d.grossMarginPercent }))
    exportToExcel(exportData, generateFilename('GrossMarginByBranch', engagementId, 'xlsx'), 'Gross Margin by Branch')
  }

  const handleExportPNG = () => {
    exportToPNG('gross-margin-branch-chart', generateFilename('GrossMarginByBranch', engagementId, 'png'))
  }

  return (
    <Card id="gross-margin-branch-chart">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">Gross Margin by Branch</CardTitle>
            <CardDescription>Gross margin % per branch</CardDescription>
          </div>
          <ChartExportMenu
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            onExportPNG={handleExportPNG}
          />
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="flex items-center justify-center h-[200px]">
            <p className="text-sm text-muted-foreground">No branch data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data} margin={{ top: 20, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis
                dataKey="branch"
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={v => `${v}%`}
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                axisLine={false}
                tickLine={false}
                domain={[
                  (dataMin: number) => Math.min(0, dataMin),
                  (dataMax: number) => Math.max(100, dataMax),
                ]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="grossMarginPercent" radius={[4, 4, 0, 0]} maxBarSize={48}>
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={BRANCH_COLORS[index % BRANCH_COLORS.length]}
                    opacity={0.85}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
